import { useInfiniteQuery } from "@tanstack/react-query";
import { playlistTagService } from "../api/services/playlistTagService";
import type {
  PlaylistTag,
  PlaylistTagFilters,
  PlaylistTagsResponse,
} from "../schemas/playlistTagSchema";
import { usePlaylistTags } from "./usePlaylistTags";
import { DEFAULT_PAGE_SIZE } from "../constants";

export type PlaylistTagsInfiniteFilters = Omit<
  PlaylistTagFilters,
  "page" | "pageSize"
> & {
  pageSize?: number;
};

export const usePlaylistTagsInfinite = (
  filters: PlaylistTagsInfiniteFilters = {},
) => {
  const { pageSize = DEFAULT_PAGE_SIZE, ...rest } = filters;
  const mutations = usePlaylistTags(undefined, { enabled: false });

  const query = useInfiniteQuery<PlaylistTagsResponse, Error>({
    queryKey: ["playlistTags", "infinite", { ...rest, pageSize }],
    queryFn: ({ pageParam }) =>
      playlistTagService.getAll({
        ...rest,
        page: pageParam as number,
        pageSize,
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage.hasNextPage ? (lastPage.page ?? 0) + 1 : undefined,
  });

  const playlistTags: PlaylistTag[] =
    query.data?.pages.flatMap((p) => p.playlistTags) ?? [];
  const total = query.data?.pages[0]?.total ?? 0;

  return {
    ...mutations,
    playlistTags,
    total,
    isLoading: query.isLoading || mutations.isLoading,
    isFetchingNextPage: query.isFetchingNextPage,
    hasNextPage: !!query.hasNextPage,
    fetchNextPage: query.fetchNextPage,
    refetch: query.refetch,
  };
};
